import React, { useState } from "react";

export function NfcReader() {
  const [status, setStatus] = useState("Ready");
  const [records, setRecords] = useState<string[]>([]);
  // เพิ่ม state สำหรับเก็บ txHash
  const [txHash, setTxHash] = useState<string | null>(null); 
  
  const API_ENDPOINT = "https://bless-commodity-apps-benjamin.trycloudflare.com/send-native";
  const TO_ADDRESS = "0x000000000000000000000000000000000000dead";
  const AMOUNT = 0.000001;

  const handleScanAndSend = async () => {
    // รีเซ็ตค่าก่อนเริ่มสแกน
    setStatus("Ready");
    setRecords([]);
    setTxHash(null); 
    
    // 1. ตรวจสอบการรองรับ Web NFC
    if (!("NDEFReader" in window)) {
      setStatus("❌ Web NFC ไม่รองรับบนอุปกรณ์/เบราว์เซอร์นี้");
      return;
    }

    try {
      const ndef = new (window as any).NDEFReader();
      await ndef.scan();
      setStatus("✅ เริ่มสแกนแล้ว... แตะแท็ก NFC");

      // 2. จัดการเมื่ออ่านแท็กได้
      ndef.onreading = async (event: any) => {
        ndef.onreading = null; 
        ndef.onerror = null;   

        const decoder = new TextDecoder();
        let daccPublicKeyFromNFC: string | null = null; 

        for (const record of event.message.records) {
          const isJsonMime = record.mediaType === "application/json";
          
          if (isJsonMime) {
            try {
              const payloadText = decoder.decode(record.data);
              const parsed = JSON.parse(payloadText);
              
              // ดึง daccPublickey จาก NFC (สมมติว่าเป็นค่า 'id')
              if (parsed.id) { 
                daccPublicKeyFromNFC = parsed.id;
                break; 
              }
            } catch (err) {
              console.log("Not parsable JSON or missing 'id' key:", decoder.decode(record.data));
            }
          }
        }

        if (!daccPublicKeyFromNFC) {
          setStatus("❌ ไม่พบข้อมูล NFC ที่ถูกต้อง: ไม่พบ daccPublickey");
          return;
        }

        // 3. แสดงข้อมูลที่อ่านได้
        setRecords([JSON.stringify({ daccPublickey: daccPublicKeyFromNFC }, null, 2)]);
        
        // 4. POP-UP ให้กรอกรหัสผ่าน
        const password = prompt("✅ สแกนสำเร็จ! กรุณากรอกรหัสผ่านเพื่อดำเนินการโอน:");

        if (!password) {
          setStatus("🛑 ยกเลิก: ผู้ใช้ไม่ได้กรอกรหัสผ่าน");
          return;
        }
        
        setStatus("📡 กำลังส่ง Transaction...");

        // 5. ส่งข้อมูลไปยัง API และจัดการผลลัพธ์
        try {
          const bodyPayload = {
            daccPublickey: daccPublicKeyFromNFC, 
            passwordSecretkey: password,         
            to: TO_ADDRESS,
            amount: AMOUNT,
          };

          const response = await fetch(API_ENDPOINT, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(bodyPayload),
            }
          );

          const result = await response.json();
          console.log("API Response:", result);

          // === เริ่มส่วนการจัดการ txHash ที่เพิ่มเข้ามา ===
          const receivedTxHash = result.txHash;

          if (receivedTxHash) {
            setTxHash(receivedTxHash);
            setStatus("✅ Transaction ถูกส่งแล้ว! (โอนสำเร็จ)");
          } else {
            // กรณีที่ไม่มี txHash ส่งกลับมา หรือค่าว่าง (อาจจะเป็น error message)
            setTxHash(null);
            setStatus(`❌ โอนไม่สำเร็จ! (ไม่มี txHash) ข้อความตอบกลับ: ${result.message || JSON.stringify(result)}`);
          }
          // === สิ้นสุดส่วนการจัดการ txHash ที่เพิ่มเข้ามา ===
          
        } catch (err: any) {
          console.error(err);
          setStatus(`⚠️ เกิดข้อผิดพลาดในการส่ง Transaction: ${err.message}`);
        }
      };
      
      // 6. จัดการเมื่อเกิดข้อผิดพลาดในการสแกน
      ndef.onerror = (event: any) => {
          setStatus(`⚠️ ข้อผิดพลาดในการอ่าน NFC: ${event.message}`);
      };
      
    } catch (err: any) {
      setStatus(`⚠️ ข้อผิดพลาด: ${err.message}`);
    }
  };

  return (
    <div className="mt-8 flex flex-col items-center">
      <button
        onClick={handleScanAndSend}
        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition"
      >
        Scan NFC & Send
      </button>

      <p className={`mt-4 font-bold ${txHash ? 'text-green-500' : 'text-red-500'}`}>
        {status}
      </p>

      {/* ส่วนแสดง txHash ที่เพิ่มเข้ามา */}
      {txHash && (
        <div className="mt-4 bg-gray-700 p-4 rounded-lg text-left w-full max-w-md">
          <h3 className="text-lg font-bold mb-2 text-white">Transaction Hash (txHash):</h3>
          <p className="text-sm text-yellow-300 break-words">
            {txHash}
          </p>
        </div>
      )}
      {/* สิ้นสุดส่วนแสดง txHash */}

      {records.length > 0 && (
        <div className="mt-4 bg-gray-900 p-4 rounded-lg text-left w-full max-w-md">
          <h3 className="text-lg font-bold mb-2 text-white">NFC Data (daccPublickey):</h3>
          {records.map((r, i) => (
            <p key={i} className="text-sm text-gray-200 break-words">
              {r}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}